/**
 * OTP Send Flow Hook
 *
 * Manages the send → countdown → resend lifecycle for SMS/Email OTP delivery.
 * Extracted from palm-web's MfaChallengeForm for reuse across all apps.
 *
 * @module @puffin-core/mfa-ui/hooks/useOtpSendFlow
 */

import { useState, useCallback } from 'react';
import { useResendCountdown } from './useResendCountdown.js';
import { SEND_FAILURE_REASONS, provenRetryAfterOf, sendFailureOf } from '../sendFailure.js';

/**
 * The send-otp answer for a code text whose outcome is NOT KNOWN (409, LIFE-SHORTLISTED-001 Q-LS-74): PortPuffin's request
 * may have reached the carrier and its answer was lost, so the text was never sent again. It is SENT, outcome unknown:
 * the code screen opens and a status line says the code may already be on its way — never a failure, which would invite
 * an immediate second code.
 */
const SMS_SEND_IN_DOUBT = 'SMS_SEND_IN_DOUBT';

/** Each reason a send failed (`./send-failure`, the ONE judgement) → this package's leaf. */
const SEND_FAILURE_LEAVES = Object.freeze({
  [SEND_FAILURE_REASONS.OPTED_OUT_BY_COMPANY]: 'error_sms_opted_out_by_company',
  [SEND_FAILURE_REASONS.CHALLENGE_INVALID]: 'error_challenge_invalid',
  [SEND_FAILURE_REASONS.CHALLENGE_EXPIRED]: 'error_challenge_expired',
  [SEND_FAILURE_REASONS.NOT_ENROLLED]: 'error_method_not_enrolled',
  [SEND_FAILURE_REASONS.EMAIL_UNAVAILABLE]: 'error_email_unavailable',
  [SEND_FAILURE_REASONS.TOO_SOON]: 'error_send_too_soon',
  [SEND_FAILURE_REASONS.FAILED]: 'error_send_failed',
});

/**
 * What the person reads when a send fails, chosen by the ONE judgement's reason (`sendFailureOf`), never the thrown
 * message. A code refused because their number texted STOP says so, and names the number to text START to (owner
 * ruling 2026-10-06, verbatim: "Keep refusal, explain it (Recommended)"). The number reads as PortPuffin sent it
 * (E.164): mfa-ui ships in apps that bundle no phone formatter, and pulling one in to pretty-print a single number
 * doubled their vendor chunk (measured 2026-10-06: vendor-misc 361 KB against a 179 KB budget in seven apps).
 *
 * @param {*} err - What the send threw (an auth-client error carries `errorCode` / `errorDetails`)
 * @param {(key: string, params?: Object) => string} t - The MFA strings' translate function
 * @returns {string}
 */
export function describeSendFailure(err, t) {
  const { reason, params } = sendFailureOf(err);
  if (reason === SEND_FAILURE_REASONS.OPTED_OUT) {
    return params.number
      ? t('error_sms_opted_out', { number: params.number })
      : t('error_sms_opted_out_no_number');
  }
  return reason === SEND_FAILURE_REASONS.TOO_SOON
    ? t(SEND_FAILURE_LEAVES[reason], params)
    : t(SEND_FAILURE_LEAVES[reason]);
}

/**
 * Hook for managing OTP send/resend with countdown timer.
 *
 * @param {Object} options
 * @param {(method: string) => Promise<void>} options.onSendOtp - Callback to send OTP
 * @param {string} options.method - Current OTP method ('sms' or 'email')
 * @param {number} [options.cooldown=60] - Cooldown in seconds between sends
 * @param {(key: string, params?: Object) => string} options.t - The MFA strings' translate function
 * @returns {{
 *   sent: boolean,
 *   sending: boolean,
 *   sendError: string|null,
 *   sendNotice: string|null,
 *   secondsRemaining: number,
 *   send: () => Promise<void>,
 *   resend: () => Promise<void>,
 *   reset: () => void,
 * }}
 */
export function useOtpSendFlow({ onSendOtp, method, cooldown = 60, t }) {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  // A status line, never an error: the last send may already be on its way (SMS_SEND_IN_DOUBT).
  const [sendNotice, setSendNotice] = useState(null);

  const {
    secondsRemaining,
    start: startCountdown,
    stop: stopCountdown,
  } = useResendCountdown(cooldown);

  const executeSend = useCallback(async () => {
    // `sending` is the anti-double-send guard: it is load-bearing (an OTP send
    // is a rate-limited, cost-bearing side effect) and stays in the dep array so
    // the closure always sees the current value. Do not remove or hoist it.
    if (!onSendOtp || sending) {
      return;
    }
    setSending(true);
    setSendError(null);
    setSendNotice(null);
    try {
      await onSendOtp(method);
      setSent(true);
      startCountdown();
    } catch (err) {
      if (err?.errorCode === SMS_SEND_IN_DOUBT) {
        setSent(true);
        setSendNotice(t('notice_sms_in_doubt'));
        startCountdown();
      } else {
        setSendError(describeSendFailure(err, t));
        const retryAfter = provenRetryAfterOf(err);
        if (retryAfter !== null) startCountdown(retryAfter);
      }
    } finally {
      setSending(false);
    }
  }, [onSendOtp, method, sending, startCountdown, t]);

  const reset = useCallback(() => {
    setSent(false);
    setSending(false);
    setSendError(null);
    setSendNotice(null);
    stopCountdown();
  }, [stopCountdown]);

  return {
    sent,
    sending,
    sendError,
    sendNotice,
    secondsRemaining,
    send: executeSend,
    resend: executeSend,
    reset,
  };
}
